import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PRESS, CONTACT } from "../data/content";
import { PageShell, ShellSection } from "./PageShell";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const Block = ({ block }) => {
    if (typeof block === "string")
        return <p className="text-sm md:text-base leading-relaxed text-thg-secondary">{block}</p>;
    if (block.h)
        return <h2 className="mt-10 font-display text-xl md:text-2xl font-medium text-white">{block.h}</h2>;
    if (block.q)
        return (
            <blockquote className="border-l-2 border-thg-red pl-6 py-1">
                <p className="font-display text-lg md:text-xl leading-relaxed text-white">&ldquo;{block.q}&rdquo;</p>
                {block.by && <cite className="mt-3 block text-xs tracking-[0.15em] uppercase not-italic text-thg-tertiary">{block.by}</cite>}
            </blockquote>
        );
    if (block.imgs)
        return (
            <figure data-testid="article-phone-gallery">
                <div className="grid grid-cols-3 gap-4 md:gap-6">
                    {block.imgs.map((img) => (
                        <div key={img.src} className="rounded-[1.4rem] border border-thg-border bg-[#050505] p-1.5">
                            <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-auto rounded-[1.1rem]" />
                        </div>
                    ))}
                </div>
                {block.caption && (
                    <figcaption className="mt-4 text-xs leading-relaxed text-thg-tertiary">{block.caption}</figcaption>
                )}
            </figure>
        );
    if (block.link)
        return (
            <p className="text-sm md:text-base leading-relaxed text-thg-secondary">
                {block.p && <span>{block.p} </span>}
                <a
                    href={block.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="article-inline-link"
                    className="text-white underline decoration-thg-red underline-offset-4 hover:text-thg-secondary transition-colors duration-300"
                >
                    {block.link.text}
                </a>
            </p>
        );
    return null;
};

export default function NewsArticlePage() {
    const { slug } = useParams();
    const article = PRESS.find((a) => a.slug === slug);
    usePageMeta(
        article ? article.metaTitle || `${article.title} — Titan Haven Group LLC` : "News & Insights — Titan Haven Group LLC",
        article?.metaDescription || article?.text,
    );
    if (!article) return <Navigate to="/news" replace />;

    return (
        <PageShell
            testId={`news-article-${article.slug}`}
            overline={`${article.type} · ${article.date}`}
            title={article.title}
            lede={article.text}
        >
            <ShellSection>
                {article.update && (
                    <Reveal>
                        <div className="mb-10 max-w-3xl border border-thg-border bg-thg-surface p-6 md:p-8" data-testid="article-update-note">
                            <p className="text-xs tracking-[0.2em] uppercase text-thg-tertiary">Update — {article.update.date}</p>
                            <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{article.update.text}</p>
                        </div>
                    </Reveal>
                )}
                <article className="max-w-3xl space-y-6" data-testid="article-body">
                    {article.body.map((b, i) => (
                        <Block key={i} block={b} />
                    ))}
                </article>
                <div className="mt-12 pt-8 border-t border-thg-border max-w-3xl">
                    <p className="text-xs leading-relaxed text-thg-tertiary">
                        Media contact: Titan Haven Group LLC —{" "}
                        <a href={`mailto:${CONTACT.email}`} className="text-thg-secondary hover:text-white transition-colors duration-300">
                            {CONTACT.email}
                        </a>
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-6">
                        {article.href && (
                            <a
                                href={article.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="article-external-link"
                                className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                            >
                                {article.linkLabel || "Related destination"} <ArrowUpRight size={15} />
                            </a>
                        )}
                        <Link
                            to="/news"
                            data-testid="article-back-link"
                            className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                        >
                            <ArrowLeft size={15} /> All news & insights
                        </Link>
                    </div>
                </div>
            </ShellSection>
        </PageShell>
    );
}
